import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface UploadStudentProfilePictureParams {
  studentId: string;
  fileType: string;
  file: File;
}

const getSignedUrl = async (studentId: string, filename: string) => {
  const response = await baseAPI.post<{ signedUrl: string }>(
    '/cloud/generate-signed-url',
    {
      userId: studentId,
      filename,
    },
  );
  return response.data.signedUrl;
};

const uploadFileToGcp = (file: File, signedUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();

      reader.onloadend = async () => {
        const binaryData = reader.result;

        const config = {
          method: 'put',
          maxBodyLength: Infinity,
          url: signedUrl,
          headers: {
            'Content-Type': file.type,
          },
          data: binaryData,
        };

        try {
          await axios.request(config);
          console.log('Successfully uploaded file to GCP');
          resolve();
        } catch (innerError) {
          if (axios.isAxiosError(innerError)) {
            console.error(
              'Error uploading file to GCP:',
              innerError.response?.data || innerError.message,
            );
            reject(
              new Error(
                `Upload failed: ${innerError.response?.status} - ${innerError.response?.data || innerError.message}`,
              ),
            );
          } else {
            reject(innerError);
          }
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file for upload.'));
      };

      reader.readAsArrayBuffer(file); // Read the file as an ArrayBuffer
    } catch (error) {
      console.error('Error preparing file for upload:', error);
      reject(error);
    }
  });
};

const syncWithBackend = async (studentId: string, userType: string) => {
  try {
    const response = await baseAPI.get(
      `/cloud/get-signed-url?fileName=${userType}&userId=${studentId}`,
    );
    console.log('Sync response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Sync with backend failed:', error);
    throw error;
  }
};

export const uploadStudentProfilePicture = async ({
  studentId,
  fileType,
  file,
}: UploadStudentProfilePictureParams) => {
  try {
    // 1. Get signed URL for uploading
    const filename = `profile.${fileType}`;
    const signedUrl = await getSignedUrl(studentId, filename);
    console.log('Got upload signed URL');

    // 2. Upload to GCP using signed URL (must wait for upload to complete)
    await uploadFileToGcp(file, signedUrl);
    console.log('File uploaded successfully to GCP');

    // 3. Wait a moment to ensure file is fully propagated in GCS
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // 4. Sync with backend to get the read signed URL and update student record
    await syncWithBackend(studentId, 'student');
    console.log('Synced with backend successfully');
  } catch (error) {
    console.error('Error in uploadStudentProfilePicture:', error);
    throw error;
  }
};

export const useUploadStudentProfilePictureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UploadStudentProfilePictureParams) =>
      uploadStudentProfilePicture(params),
    onSuccess: (_, params) => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENTS, params.studentId],
      });
    },
  });
};
